import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useState } from "react";
import {
  LayoutDashboard, Sparkles, HeartHandshake, BookOpen, CreditCard,
  Shield, LogOut, Menu, X, Bot, CalendarHeart, Stethoscope, Scale, Pill,
} from "lucide-react";
import { BloomHerLogo } from "@/components/BloomHerLogo";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/ritual", label: "Daily Ritual", icon: Sparkles, testid: "nav-ritual" },
  { to: "/shapeup", label: "ShapeUp", icon: Scale, testid: "nav-shapeup" },
  { to: "/faith", label: "Faith & Prayer", icon: HeartHandshake, testid: "nav-faith" },
  { to: "/library", label: "Library", icon: BookOpen, testid: "nav-library" },
  { to: "/assistant", label: "AI Assistant", icon: Bot, testid: "nav-assistant" },
  { to: "/coaching", label: "Coaching", icon: CalendarHeart, testid: "nav-coaching" },
  { to: "/care", label: "Clinical Care", icon: Stethoscope, testid: "nav-care" },
  { to: "/programs", label: "Treatments & Programs", icon: Pill, testid: "nav-programs" },
  { to: "/membership", label: "Membership", icon: CreditCard, testid: "nav-membership" },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const items = [...navItems];
  if (user?.role === "admin") {
    items.push({ to: "/admin", label: "Admin", icon: Shield, testid: "nav-admin" });
  }

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const NavLinks = ({ onClick }) => (
    <>
      {items.map((it) => {
        const active = location.pathname === it.to;
        const Icon = it.icon;
        return (
          <Link
            key={it.to}
            to={it.to}
            data-testid={it.testid}
            onClick={onClick}
            className={`flex items-center gap-3 rounded-full px-4 py-3 text-sm font-sans transition-all ${
              active
                ? "bg-wine text-white shadow-[0_8px_30px_rgba(107,37,84,0.18)]"
                : "text-[#6E5862] hover:bg-taupe/50 hover:text-[#3A1230]"
            }`}
          >
            <Icon className="h-[18px] w-[18px]" strokeWidth={1.5} />
            {it.label}
          </Link>
        );
      })}
    </>
  );

  return (
    <div className="min-h-screen bg-cream">
      {/* Mobile header */}
      <header className="lg:hidden sticky top-0 z-50 flex items-center justify-between bg-cream/80 backdrop-blur-xl border-b border-taupe/50 px-5 py-4">
        <Link to="/dashboard" className="flex items-center gap-2" data-testid="brand-logo-mobile">
          <BloomHerLogo size="text-2xl" />
        </Link>
        <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="text-wine">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </header>

      {open && (
        <div className="lg:hidden fixed inset-0 z-40 bg-cream pt-20 px-5 space-y-2">
          <NavLinks onClick={() => setOpen(false)} />
          <button
            data-testid="logout-mobile"
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-full px-4 py-3 text-sm text-[#6E5862] hover:bg-taupe/50"
          >
            <LogOut className="h-[18px] w-[18px]" strokeWidth={1.5} /> Sign out
          </button>
        </div>
      )}

      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex flex-col fixed inset-y-0 w-72 border-r border-taupe/50 bg-cream/60 px-6 py-8">
          <Link to="/dashboard" className="flex items-center gap-2 mb-12 px-2" data-testid="brand-logo">
            <BloomHerLogo size="text-3xl" />
          </Link>
          <nav className="space-y-1.5 flex-1">
            <NavLinks />
          </nav>
          <div className="mt-6 border-t border-taupe/60 pt-6">
            <div className="flex items-center gap-3 px-2 mb-4">
              <div className="h-10 w-10 rounded-full bg-wine/10 flex items-center justify-center text-wine font-serif text-lg">
                {user?.name?.[0]?.toUpperCase() || "B"}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-[#3A1230] truncate">{user?.name}</p>
                <p className="text-xs text-[#9B7E8D] capitalize">{user?.role}</p>
              </div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="flex w-full items-center gap-3 rounded-full px-4 py-2.5 text-sm text-[#6E5862] hover:bg-taupe/50 transition-colors"
            >
              <LogOut className="h-[18px] w-[18px]" strokeWidth={1.5} /> Sign out
            </button>
          </div>
        </aside>

        <main className="flex-1 lg:ml-72 px-5 sm:px-8 lg:px-12 py-8 lg:py-12 max-w-6xl">
          {children}
        </main>
      </div>
    </div>
  );
}
